import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import moment from "moment";
import { useGetDatesThatMatterFilterOptions } from "@/utils/react-query-hooks/lenses";
import { periodGrain, periodKey, previousPeriodKey } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/periodKeys";

/**
 * The period selection every period-scoped page reads and writes: the
 * period, and the period its changes are measured against ("x vs y"). One
 * provider above the routes, so moving between the Overview, Topics and
 * Priorities keeps the same selection.
 *
 *   period          the selected period option; default the latest month
 *   comparePeriod   the option compared with; default the one immediately
 *                   before the period (null when none exists)
 *   compareKey      the comparison as the scorer names it ("2026-06")
 *   compareOptions  what can be compared with: earlier periods of the same
 *                   kind as the selected one
 *   periodOptions   every period on offer, with every month in the corpus's
 *                   date range present (see withEveryMonthInRange)
 *   setPeriod(value) / setCompare(value)   picking a period resets the
 *                   comparison to the one before it
 *   periodLoading, dateFieldMap
 *
 * `period` is null both while the options load and when the tenant has no
 * dated observation; `periodLoading` tells the two apart.
 */
const PeriodSelectionContext = createContext(null);

export const PeriodSelectionProvider = ({ children }) => {
  const [selection, setSelection] = useState({ periodValue: null, compareValue: null });
  const setPeriod = useCallback((periodValue) => setSelection({ periodValue, compareValue: null }), []);
  const setCompare = useCallback((compareValue) => setSelection((s) => ({ ...s, compareValue })), []);
  const value = useMemo(() => ({ ...selection, setPeriod, setCompare }), [selection, setPeriod, setCompare]);
  return <PeriodSelectionContext.Provider value={value}>{children}</PeriodSelectionContext.Provider>;
};

const MONTH_VALUE = /^month_\d{4}_\d{2}$/;

const latestMonthOf = (options) => {
  const months = options.filter((o) => MONTH_VALUE.test(o.value)).sort((a, b) => a.value.localeCompare(b.value));
  return months[months.length - 1] || null;
};

// Every month from the corpus's first dated observation to its last, newest
// first — months with nothing in them included. The period options from the
// BE list only months that HAVE data; here a gap must still be pickable,
// and "the month before" must still exist to compare with. A month the BE
// already offers is kept as is; a missing one is drawn here with the same
// shape (edges in the viewer's timezone, `to` = the next month's start, the
// BE's interval for a month-long range).
const MAX_MONTHS = 60;
const withEveryMonthInRange = (options, globalDateRange) => {
  const firstMonthAt = options.findIndex((o) => MONTH_VALUE.test(o.value));
  if (firstMonthAt < 0 || !globalDateRange?.min || !globalDateRange?.max) return options;
  const offered = Object.fromEntries(options.map((o) => [o.value, o]));
  const months = [];
  const first = moment(globalDateRange.min).startOf("month");
  const cursor = moment(globalDateRange.max).startOf("month");
  while (!cursor.isBefore(first) && months.length < MAX_MONTHS) {
    const value = `month_${cursor.format("YYYY_MM")}`;
    months.push(
      offered[value] || {
        value,
        display: cursor.format("MMM YYYY"),
        from: cursor.toISOString(),
        to: cursor.clone().add(1, "month").toISOString(),
        interval: "week",
      }
    );
    cursor.subtract(1, "month");
  }
  const rest = options.filter((o) => !MONTH_VALUE.test(o.value));
  const before = rest.filter((o) => options.indexOf(o) < firstMonthAt);
  const after = rest.filter((o) => options.indexOf(o) > firstMonthAt);
  return [...before, ...months, ...after];
};

export const usePeriodSelection = () => {
  const ctx = useContext(PeriodSelectionContext);
  if (!ctx) throw new Error("usePeriodSelection needs a PeriodSelectionProvider above it");
  const { periodValue, compareValue, setPeriod, setCompare } = ctx;
  const { data: dateFilterOptions, isLoading: periodLoading } = useGetDatesThatMatterFilterOptions();

  const periodOptions = useMemo(
    () => withEveryMonthInRange(dateFilterOptions?.periods || [], dateFilterOptions?.globalDateRange),
    [dateFilterOptions]
  );
  const period = useMemo(() => periodOptions.find((o) => o.value === periodValue) || latestMonthOf(periodOptions), [periodOptions, periodValue]);

  // What the period can be compared with: earlier periods of its own kind,
  // newest first. (A period of another kind — "this year", "all time" —
  // has no like-for-like neighbours in the list, so nothing is offered.)
  const compareOptions = useMemo(() => {
    const grain = periodGrain(period);
    if (!grain || !period?.from) return [];
    return periodOptions
      .filter((o) => periodGrain(o) === grain && o.from && o.from < period.from)
      .sort((a, b) => (a.from < b.from ? 1 : -1));
  }, [periodOptions, period]);

  // The one picked, else the one immediately before the period.
  const comparePeriod = useMemo(() => {
    if (!period) return null;
    const previousKey = previousPeriodKey(period);
    return compareOptions.find((o) => o.value === compareValue) || compareOptions.find((o) => periodKey(o) === previousKey) || null;
  }, [compareOptions, compareValue, period]);
  // The scorer's name for it. No comparison when nothing of the period's
  // kind lies before it: the scorer then reads "no earlier period", as the
  // picker shows.
  const compareKey = useMemo(() => (comparePeriod ? periodKey(comparePeriod) : null), [comparePeriod]);

  return {
    period,
    comparePeriod,
    compareKey,
    compareOptions,
    periodOptions,
    periodLoading,
    dateFieldMap: dateFilterOptions?.dateFieldMap,
    setPeriod,
    setCompare,
  };
};
